import { motion } from "framer-motion";
import { MapPin, Phone, Mail, ExternalLink, Navigation, Clock } from "lucide-react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { Button } from "@/components/ui/button";
import { easeOut } from "@/components/common/Reveal";
import { CENTRES, HORAIRES } from "@/data/site";
import { useLead } from "@/components/common/LeadModal";

export default function Centres() {
  const { openLead } = useLead();
  return (
    <>
      <Seo
        title="Nos centres de formation"
        description="Les centres de formation EDUCASER GROUP en Île-de-France : Palaiseau (91), Noisy-le-Sec (93), Meaux (77) et Choisy-le-Roi (94). Adresses, téléphones et coordonnées complètes."
        path="/nos-centres"
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Nos centres" }]}
        eyebrow="Où nous trouver"
        title="Nos centres de formation"
        intro="Sept centres pour vous former au plus près de chez vous. Chaque centre est un organisme de formation juridiquement indépendant, membre du regroupement EDUCASER GROUP."
      />

      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-10 px-5 lg:px-8">
          {CENTRES.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="grid grid-cols-1 overflow-hidden rounded-3xl border border-slate-200 md:grid-cols-2"
              data-testid={`centre-${c.id}`}
            >
              {/* Visual: real photo if available, else stylized map */}
              {c.image ? (
                <div className="relative min-h-[240px] overflow-hidden bg-brand-blue" data-testid={`centre-image-${c.id}`}>
                  <img
                    src={c.image}
                    alt={`Centre de formation EDUCASER — ${c.ville}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-brand-blue/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
                    <p className="font-display text-2xl font-bold text-white drop-shadow">{c.ville}</p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.mapQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-4 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/30"
                      data-testid={`centre-map-${c.id}`}
                    >
                      <Navigation className="h-3.5 w-3.5" /> Itinéraire
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="relative flex min-h-[240px] items-center justify-center bg-brand-blue">
                  <div className="absolute inset-0 bg-grid opacity-10" />
                  <div className="relative text-center text-white">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-red shadow-lg">
                      <MapPin className="h-8 w-8" />
                    </div>
                    <p className="mt-4 font-display text-2xl font-bold">{c.ville}</p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.mapQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition-colors hover:bg-white/20"
                      data-testid={`centre-map-${c.id}`}
                    >
                      <Navigation className="h-3.5 w-3.5" /> Itinéraire
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* Details */}
              <div className="p-8 lg:p-10">
                <div className="flex flex-col items-start gap-1.5">
                  {(Array.isArray(c.tag) ? c.tag : [c.tag]).map((t) => (
                    <span key={t} className="inline-block rounded-full bg-brand-red/10 px-3 py-1 text-xs font-medium text-brand-red">
                      {t}
                    </span>
                  ))}
                </div>
                <h2 className="mt-4 font-display text-2xl font-bold text-brand-blue">{c.nom}</h2>
                <ul className="mt-5 space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" /> <span className="min-w-0 break-words">{c.adresse}</span>
                  </li>
                  {(c.phones || (c.telephone ? [{ num: c.telephone }] : [])).map((p) => (
                    <li key={p.num} className="flex items-start gap-3">
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                      <a href={`tel:${p.num.replace(/\s/g, "")}`} className="min-w-0 break-words hover:text-brand-blue">
                        {p.num}{p.label ? ` — ${p.label}` : ""}
                      </a>
                    </li>
                  ))}
                  {(c.emails || (c.email ? [{ addr: c.email }] : [])).map((m) => (
                    <li key={m.addr} className="flex items-start gap-3">
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                      <a href={`mailto:${m.addr}`} className="min-w-0 break-words hover:text-brand-blue">
                        {m.addr}{m.label ? ` — ${m.label}` : ""}
                      </a>
                    </li>
                  ))}
                  <li className="flex items-start gap-3" data-testid={`centre-horaires-${c.id}`}>
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                    <span className="min-w-0 break-words">{HORAIRES}</span>
                  </li>
                </ul>
                <Button
                  onClick={() => openLead("rappel", { message: `Centre de ${c.ville}` })}
                  className="mt-7 rounded-full bg-brand-blue hover:bg-brand-blue-light"
                  data-testid={`centre-cta-${c.id}`}
                >
                  Être rappelé
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
}
